import React from 'react';
import { useSelector } from 'react-redux';
import { useEffectOnce } from 'react-use';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { CampaignDTO } from '../../../../api/useCampaignsQuery';
import { CampaignAccessDTO, useCampaignAccessQuery } from '../../../../api/useCampaignAccessQuery';
import {
  adviserSelector,
  isDisableAdviserSuggestionsSelector,
} from '../../../../../AdviserArea/AdviserAreaSelector';
import { ErrorState } from '../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Loading } from '../../../../../CommonComponents/OPL/Loading/Loading';
import { SearchAdvisors } from './components/SearchAdvisors/SearchAdvisors';
import { SameOrgAdvisors } from './components/SameOrgAdvisors/SameOrgAdvisors';
import { CampaignAccessTable } from './components/CampaignAccessTable/CampaignAccessTable';
import { Leads, LEADS_OPTIONS } from './CampaignAccess.meta';

interface Props {
  campaign: CampaignDTO;
  setCampaign(campaign: CampaignDTO): void;
  campaignAccess: CampaignAccessDTO | null;
  setCampaignAccess(campaignAccess: CampaignAccessDTO): void;
  availableLeadsAccess: number[];
}

export const CampaignAccess: React.FC<Props> = ({
  campaign,
  setCampaign,
  campaignAccess,
  setCampaignAccess,
  availableLeadsAccess,
}) => {
  const { isLoading, isError, refetch } = useCampaignAccessQuery({
    campaignId: campaign.id,
    onSuccess(access) {
      setCampaignAccess(access);
    },
  });

  const loggedAdviser = useSelector(adviserSelector);

  const isDisableAdviserSuggestions = useSelector(isDisableAdviserSuggestionsSelector);

  const onLeadsAccessChange = (leadsAccess: Leads) => {
    setCampaign({ ...campaign, leadsAccess });

    if (leadsAccess === Leads.SpecificAdvisors && !campaign.id) {
      setCampaignAccess({
        canTransferOwnership: true,
        relationships: [
          {
            adviserId: loggedAdviser.id,
            firstName: loggedAdviser.firstName,
            lastName: loggedAdviser.lastName,
            isOwner: true,
            isManager: false,
            isRemovable: false,
          },
        ],
      });
    }
  };

  useEffectOnce(() => {
    if (availableLeadsAccess.length === 1 && availableLeadsAccess[0] === Leads.SpecificAdvisors) {
      onLeadsAccessChange(Leads.SpecificAdvisors);
    }
  });

  if (isLoading) {
    return (
      <Box my="56">
        <Loading text="Loading..." size="small" mx="auto" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box my="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  const leadsOptions = LEADS_OPTIONS.filter(({ value }) => availableLeadsAccess.includes(value));

  return (
    <Box my="16">
      {leadsOptions.length > 1 && (
        <Select
          label="Who has access"
          value={leadsOptions.find(({ value }) => value === campaign.leadsAccess)}
          options={leadsOptions}
          onChange={opt => {
            if (opt) {
              onLeadsAccessChange(opt.value);
            }
          }}
          menuPlacement="top"
        />
      )}

      {campaign.leadsAccess === Leads.SpecificAdvisors && campaignAccess && (
        <>
          {isDisableAdviserSuggestions ? (
            <SearchAdvisors campaignAccess={campaignAccess} setCampaignAccess={setCampaignAccess} />
          ) : (
            <SameOrgAdvisors
              campaignAccess={campaignAccess}
              setCampaignAccess={setCampaignAccess}
            />
          )}

          <CampaignAccessTable
            campaignAccess={campaignAccess}
            setCampaignAccess={setCampaignAccess}
          />
        </>
      )}
    </Box>
  );
};
